import React, { useState } from 'react';
import { Flex } from 'antd';
import { useIsMobile } from '../../../services/responsiveUtils';
import { useTranslation } from 'react-i18next';

import WizardHeader from './Header/WizardHeader.component';
import WizardNavigation from './Navigation/WizardNavigation.component';
import { WizardStep, WizardOptions } from './types';

export interface GenericWizardProps {
  steps: WizardStep[];
  pageTitle: string;
  submitButtonText: string;
  backButtonText?: string;
  nextButtonText?: string;
  backLinkButtonText?: string;
  isSubmitting?: boolean;
  options?: WizardOptions;
  onSubmit: () => void;
  onBackClick?: () => void;
  shouldDisableNext?: (currentStep: number) => boolean;
}

const GenericWizard: React.FC<GenericWizardProps> = ({
  steps,
  pageTitle,
  submitButtonText,
  backButtonText = 'Back',
  nextButtonText = 'Next',
  backLinkButtonText,
  isSubmitting = false,
  options = {},
  onSubmit,
  onBackClick,
  shouldDisableNext,
}) => {
  const { initialStep = 0, scrollToTop = true } = options;
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      if (scrollToTop) {
        window.scrollTo(0, 0);
      }
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      if (scrollToTop) {
        window.scrollTo(0, 0);
      }
    }
  };

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    }
  };

  // Преобразуем шаги для передачи в Header
  const stepsForHeader = steps.map(step => ({
    title: step.title,
    description: step.description
  }));

  return (
    <Flex vertical gap="large" style={{ width: '100%' }}>
      <WizardHeader
        pageTitle={pageTitle}
        onBack={handleBackClick}
        backButtonText={backLinkButtonText || t('back', { ns: 'buttons' })}
        steps={stepsForHeader}
        currentStep={currentStep}
        isMobile={isMobile}
      />

      {steps[currentStep]?.component}

      <WizardNavigation
        currentStep={currentStep}
        stepsCount={steps.length}
        isSubmitting={isSubmitting}
        submitButtonText={submitButtonText}
        backButtonText={backButtonText}
        nextButtonText={nextButtonText}
        onNext={goToNextStep}
        onPrev={goToPrevStep}
        onSubmit={onSubmit}
        shouldDisableNext={shouldDisableNext ? () => shouldDisableNext(currentStep) : undefined}
      />
    </Flex>
  );
};

export default GenericWizard;